// Effortdex — EV training tracker built with native Web Components.
// This file is the composition root: it wires the design system into the
// light DOM, initializes app-wide chrome (lib/shell.ts, lib/app-version.ts),
// and dispatches each route to its page module (components/pages/*.js) —
// no page-specific DOM or rendering lives here. All domain logic lives in
// lib/, and each custom element owns its own rendering. Also the one
// place that opens/closes the party create/edit dialog, in response to a
// route's `dialog` field (docs/adr/0022) — party-dialog.js itself never
// opens on its own.

import { store, prefetchService } from './lib/services.ts';
import { attachDesignSystem } from './lib/design-system.ts';
import { wireDialogCloseButtons } from './lib/dom.ts';
import * as router from './lib/router.ts';
import { setHeaderContext } from './lib/shell.ts';
import './lib/app-version.ts';
import * as parties from './components/pages/parties/parties.ts';
import * as roster from './components/pages/parties/roster.ts';
import * as pokemon from './components/pages/parties/pokemon/pokemon.ts';
import * as partyDialog from './components/pages/parties/party-dialog.ts';
import * as settings from './components/pages/settings/settings.ts';
import * as transferHub from './components/pages/transfer/transfer.ts';
import * as transferExport from './components/pages/transfer/export.ts';
import * as spriteCache from './components/pages/settings/cache.ts';
import * as importPage from './components/pages/transfer/import.ts';

// Let light-DOM markup (the party dialog) use the same .ds-field/.ds-btn
// primitives every shadow-DOM component uses — one shared stylesheet.
attachDesignSystem(document);
wireDialogCloseButtons();

/* ------------------------------------------------------------------ */
/* Router <-> page                                                     */
/* ------------------------------------------------------------------ */

const VIEWS: HTMLElement[] = [parties.view, roster.view, pokemon.view, settings.view, transferHub.view, transferExport.view, spriteCache.view, importPage.view];
function showView(view: HTMLElement): void {
  for (const v of VIEWS) v.hidden = v !== view;
}

function render(): void {
  const { page, partySlug, pokemonUid, payload, dialog, pokemonDialog } = router.currentRoute();
  // Cleared here, set again below only for the two in-party views.
  setHeaderContext(null);

  if (page === 'settings') {
    partyDialog.closeIfOpen();
    pokemon.closeDialogsIfOpen();
    showView(settings.view);
    settings.render();
    return;
  }

  if (page === 'transfer') {
    partyDialog.closeIfOpen();
    pokemon.closeDialogsIfOpen();
    showView(transferHub.view);
    transferHub.render();
    return;
  }

  if (page === 'transfer-export') {
    partyDialog.closeIfOpen();
    pokemon.closeDialogsIfOpen();
    showView(transferExport.view);
    transferExport.render();
    return;
  }

  if (page === 'cache') {
    partyDialog.closeIfOpen();
    pokemon.closeDialogsIfOpen();
    showView(spriteCache.view);
    spriteCache.render();
    return;
  }

  if (page === 'import') {
    partyDialog.closeIfOpen();
    pokemon.closeDialogsIfOpen();
    showView(importPage.view);
    importPage.render(payload);
    return;
  }

  if (!partySlug) {
    pokemon.closeDialogsIfOpen();
    showView(parties.view);
    parties.render();
    if (dialog === 'create-party') partyDialog.openCreateDialog();
    else partyDialog.closeIfOpen();
    return;
  }

  const party = store.getPartyBySlug(partySlug);
  if (!party) {
    router.navigateHome(); // unknown/stale slug — bounce to the picker
    return;
  }
  if (store.state.activePartyId !== party.id) {
    store.setActiveParty(party.id); // triggers a 'change' -> render() again, harmlessly
    return;
  }

  if (pokemonUid) {
    const entry = party.pokemon.find((e) => e.uid === pokemonUid);
    if (!entry) {
      router.navigateToParty(party.slug); // stale link, or this Pokémon was just removed
      return;
    }
    partyDialog.closeIfOpen(); // no dialog route exists this deep — always closed here
    setHeaderContext(party.name);
    showView(pokemon.view);
    pokemon.render(party, entry, pokemonDialog);
    return;
  }

  pokemon.closeDialogsIfOpen();
  setHeaderContext(party.name);
  showView(roster.view);
  roster.render(party);
  if (dialog === 'edit-party') partyDialog.openEditDialog(party);
  else partyDialog.closeIfOpen();
}

router.onRouteChange(render);
// A route change means a new page — start it at the top, the way a real
// navigation would. Wired to route changes only, not store 'change'
// (that's data edits — scrolling to the top mid-edit would be jarring);
// the roster's own search/filter state uses replaceState, which never
// fires this.
router.onRouteChange(() => scrollTo({ top: 0 }));
// The count script (index.html) already counts the initial page load
// on its own; this only needs to cover subsequent in-app hash
// navigation, which is why it's wired to route changes specifically —
// not to store's own 'change' (data edits, not navigation) below.
//
// Imported dynamically, not statically: a static import is one failed
// network request away from taking the entire module graph down with
// it (an ad-blocker filter list blocking this file by name did exactly
// that — see lib/goatcounter-report.ts's own header comment). A
// dynamic import failing here only means pageviews go unreported;
// nothing else about the app is affected, matching this feature's own
// "no dependency on the analytics server" promise (issue #24) for the
// local file itself, not just the third-party script it loads.
let reportPageview: () => void = () => {};
import('./lib/goatcounter-report.ts')
  .then((m) => {
    reportPageview = m.trackPageview;
  })
  .catch(() => {}); // blocked/missing — analytics just doesn't happen
router.onRouteChange(() => reportPageview());
store.addEventListener('change', render);
// The constructor already loaded and projected the roster synchronously
// from localStorage; init() only warms the IndexedDB-backed caches
// (docs/adr/0025 P3). Fast (tens of ms) — the static shell from
// index.html is already painted, and the roster view is hidden until
// render() runs — so no loading screen. Awaited so the ordering matches
// P4, where init() will be what loads the roster.
await store.init();
render();

// Warms sw.js's sprite cache ahead of need (docs/adr/0011), and resumes
// any manual cache/generation run a previous page load got cut off
// mid-fetch (docs/adr/0012's "resume" addendum — the queue itself lives
// only in memory, so a refresh has no way to survive on its own) —
// deferred until the browser is idle so neither competes with first
// render.
if ('requestIdleCallback' in window) {
  requestIdleCallback(() => {
    prefetchService.start();
    prefetchService.resumeInterrupted();
  });
} else {
  setTimeout(() => {
    prefetchService.start();
    prefetchService.resumeInterrupted();
  }, 2000);
}
